'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'next/navigation';
import { Zap, Search, X, ChevronDown, LayoutGrid, Shield, SlidersHorizontal } from 'lucide-react';
import { Pagination } from '@/components/ui/Pagination';
import type { CatalogIndexItem, SortKey, TypeFilter, PlatformFilter } from '@/types/catalog';
import { CatalogCard } from './CatalogCard';
import { OfferFilter, readOfferParams, OFFER_ICONS, ALL_REGIONS, type OfferType, type RegionCode } from './OfferFilter';
import { fetchPrices } from '@/lib/priceCache';
import { LanguageFilter, ALL_LANG_TYPES, LANG_LIST, langName, type LangType } from './LanguageFilter';
import { GENRES } from './GenreFilter';
import { CAP_MAP } from '@/lib/capabilityMap';
import { usePriceConvert } from '@/lib/PriceContext';
import { t } from '@/lib/i18n';

// Детермінований формат числа (пробіл-роздільник) — однаковий на сервері й клієнті,
// щоб уникнути hydration-mismatch від toLocaleString() (різні локалі Node vs браузер).
function fmtCount(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// Локалізована назва можливості (Single Player → Одиночна гра тощо) через CAP_MAP.
function capLabel(key: string, lang: string): string {
  const c = CAP_MAP[key];
  if (c) return lang === 'ru' ? c.ru : lang === 'uk' ? c.uk : c.en;
  return CAP_FILTER_OPTIONS.find(o => o.key === key)?.label ?? key;
}

const PAGE_SIZE  = 30;
const PRICE_MAX  = 70;
const YEAR_MIN   = 2005;
const YEAR_MAX   = 2026;

const SORT_KEYS: SortKey[]        = ['popular','new','rating_desc','rating_asc','price_asc','price_desc','discount_desc','discount_asc'];
const TYPE_KEYS: TypeFilter[]     = ['all','game','dlc'];
const PLAT_KEYS: PlatformFilter[] = ['all','xsx','xone','pc','xcloud','handheld'];
const PLAT_CHIP: Record<PlatformFilter, string> = {
  all:'on', xsx:'on-xsx', xone:'on-xone', pc:'on-pc', xcloud:'on-xcloud', handheld:'on-handheld',
};
const PLAT_LABEL: Record<PlatformFilter, string> = {
  all:'', xsx:'Series X|S', xone:'Xbox One', pc:'PC', xcloud:'xCloud', handheld:'Handheld',
};

// Локалізована назва країни за ISO-кодом регіону (AR→Аргентина за мовою сайту).
function regionName(code: string, lang: string): string {
  try {
    const loc = lang === 'ru' ? 'ru-RU' : lang === 'uk' ? 'uk-UA' : 'en';
    return new Intl.DisplayNames([loc], { type: 'region' }).of(code) ?? code;
  } catch { return code; }
}

function readSort(p: URLSearchParams): SortKey {
  if (p.get('sale') === '1') return 'discount_desc';
  const v = p.get('sort') ?? '';
  return (SORT_KEYS.includes(v as SortKey) ? v : 'popular') as SortKey;
}
function readType(p: URLSearchParams): TypeFilter {
  const v = p.get('type') ?? '';
  return (TYPE_KEYS.includes(v as TypeFilter) ? v : 'all') as TypeFilter;
}
function readPlat(p: URLSearchParams): PlatformFilter {
  const v = p.get('plat') ?? '';
  return (PLAT_KEYS.includes(v as PlatformFilter) ? v : 'all') as PlatformFilter;
}

// Кеш списку студій (для автокомпліту) — окремий легкий ендпоінт, не весь каталог.
let _studiosCache: [string, StudioRole][] | null = null;
let _studiosPromise: Promise<[string, StudioRole][]> | null = null;
function loadStudios(): Promise<[string, StudioRole][]> {
  if (_studiosCache) return Promise.resolve(_studiosCache);
  if (_studiosPromise) return _studiosPromise;
  _studiosPromise = fetch('/api/catalog-studios', { credentials: 'same-origin' })
    .then(r => r.json())
    .then((data: [string, StudioRole][]) => { _studiosCache = data; return data; })
    .catch(() => []);
  return _studiosPromise;
}

// -- Price Range Slider ------------------------------------------------------
function PriceRangeSlider({ priceMin, priceMax, onMinChange, onMaxChange }: {
  priceMin: number; priceMax: number;
  onMinChange: (v: number) => void; onMaxChange: (v: number) => void;
}) {
  const pctMin = (priceMin / PRICE_MAX) * 100;
  const pctMax = (priceMax / PRICE_MAX) * 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--v4-fg-2)', whiteSpace: 'nowrap', minWidth: 84, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        ${priceMin} — {priceMax >= PRICE_MAX ? '∞' : `$${priceMax}`}
      </span>
      <div className="price-range-wrap">
        <div className="price-range-track">
          <div className="price-range-fill" style={{ left: `${pctMin}%`, right: `${100 - pctMax}%` }} />
        </div>
        <input type="range" min={0} max={PRICE_MAX} step={1} value={priceMin}
          className="price-range-input"
          style={{ zIndex: priceMin > PRICE_MAX * 0.5 ? 5 : 3 }}
          onChange={e => onMinChange(Math.min(+e.target.value, priceMax - 1))} />
        <input type="range" min={0} max={PRICE_MAX} step={1} value={priceMax}
          className="price-range-input"
          style={{ zIndex: priceMax < PRICE_MAX * 0.5 ? 5 : 3 }}
          onChange={e => onMaxChange(Math.max(+e.target.value, priceMin + 1))} />
      </div>
    </div>
  );
}

// -- Studio Autocomplete -----------------------------------------------------
type StudioRole = 'dev' | 'pub' | 'both';
const ROLE_COLOR:  Record<StudioRole, string> = { dev: '#52b043', pub: '#5cb8ff', both: '#a78bfa' };
const ROLE_BG:     Record<StudioRole, string> = { dev: 'rgba(82,176,67,.1)', pub: 'rgba(92,184,255,.1)', both: 'rgba(167,139,250,.12)' };
const ROLE_BORDER: Record<StudioRole, string> = { dev: 'rgba(82,176,67,.22)', pub: 'rgba(92,184,255,.22)', both: 'rgba(167,139,250,.25)' };

function StudioAutocomplete({ value, onChange, studios, lang }: {
  value: string;
  onChange: (s: string) => void;
  studios: [string, StudioRole][];
  lang: string;
}) {
  const [open, setOpen]   = useState(false);
  const [hiIdx, setHiIdx] = useState(-1);
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef  = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const query = value.toLowerCase().trim();
  const suggestions = useMemo(() => {
    if (query.length < 1) return [];
    return studios.filter(([name]) => name.toLowerCase().includes(query)).slice(0, 8);
  }, [studios, query]);

  const showDrop = open && suggestions.length > 0;

  function updatePos() {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  }

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  function select(name: string) {
    onChange(name);
    setOpen(false);
    setHiIdx(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown')                { e.preventDefault(); setHiIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp')             { e.preventDefault(); setHiIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && hiIdx >= 0) { e.preventDefault(); select(suggestions[hiIdx][0]); }
    else if (e.key === 'Escape')              { setOpen(false); setHiIdx(-1); }
  }

  const dropdown = showDrop && dropPos ? (
    <div style={{
      position: 'fixed',
      top: dropPos.top,
      left: dropPos.left,
      width: Math.max(dropPos.width, 240),
      maxWidth: 340,
      background: 'color-mix(in srgb, var(--v4-bg-elev) 92%, transparent)',
      backdropFilter: 'blur(22px) saturate(140%)',
      WebkitBackdropFilter: 'blur(22px) saturate(140%)',
      border: '1px solid var(--v4-border-hover)',
      borderRadius: 10,
      boxShadow: '0 8px 32px rgba(0,0,0,.65)',
      zIndex: 9999,
      overflow: 'hidden',
    }}>
      {suggestions.map(([name, role], idx) => (
        <button
          key={name}
          onMouseDown={e => { e.preventDefault(); select(name); }}
          onMouseEnter={() => setHiIdx(idx)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '8px 12px', gap: 10,
            background: idx === hiIdx ? 'var(--v4-panel-2)' : 'transparent',
            border: 'none',
            borderBottom: idx < suggestions.length - 1 ? '1px solid var(--v4-border)' : 'none',
            color: 'var(--v4-fg)', cursor: 'pointer', textAlign: 'left',
            transition: 'background .1s',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </span>
          <span style={{
            flexShrink: 0, fontSize: 10, fontWeight: 700,
            color: ROLE_COLOR[role], background: ROLE_BG[role],
            border: `1px solid ${ROLE_BORDER[role]}`,
            borderRadius: 5, padding: '2px 6px', letterSpacing: '0.02em',
          }}>
            {role === 'dev' ? t('studio_dev', lang) : role === 'pub' ? t('studio_pub', lang) : t('studio_both', lang)}
          </span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} className="fbar-studio-wrap">
      <Search size={11} className="fbar-studio-icon" />
      <input
        ref={inputRef}
        className="fbar-studio-input"
        placeholder={t('filter_studio', lang)}
        value={value}
        autoComplete="off"
        onChange={e => { onChange(e.target.value); setHiIdx(-1); setOpen(true); updatePos(); }}
        onFocus={() => { setOpen(true); updatePos(); }}
        onKeyDown={onKeyDown}
      />
      {value && (
        <button className="fbar-studio-clear" onClick={() => { onChange(''); inputRef.current?.focus(); }}>
          <X size={10} />
        </button>
      )}
      {typeof document !== 'undefined' && dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}



// -- Star Rating (click on stars, 1–5, 0 = no filter) ---------------------
function StarRating({ value, onChange, lang }: { value: number; onChange: (v: number) => void; lang?: string }) {
  const [hover, setHover] = useState(0);
  const STARS = [1, 2, 3, 4, 5];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {STARS.map(s => {
        const active = s <= (hover || value);
        return (
          <button
            key={s}
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(s === value ? 0 : s)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '1px',
              color: active ? '#f59e0b' : 'var(--v4-fg-4)',
              fontSize: 16, lineHeight: 1,
              transform: hover === s ? 'scale(1.25)' : 'scale(1)',
              transition: 'transform .15s, color .15s',
            }}
            aria-label={`★${s}`}
          >
            ★
          </button>
        );
      })}
      {value > 0 && (
        <button
          onClick={() => onChange(0)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--v4-fg-3)', padding: '1px', marginLeft: 2,
            display: 'flex', alignItems: 'center',
          }}
          aria-label={lang ? t('filter_reset', lang) : 'Reset'}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      )}
    </div>
  );
}


// -- Capability Filter Dropdown ----------------------------------------------
const CAP_FILTER_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'SinglePlayer',                label: 'Single Player' },
  { key: 'XblOnlineMultiPlayer',        label: 'Online Multiplayer (2+)' },
  { key: 'XblLocalMultiPlayer',         label: 'Local Multiplayer (2+)' },
  { key: 'XblOnlineCoop',               label: 'Online Co-op (2+)' },
  { key: 'XblLocalCoop',                label: 'Local Co-op (2+)' },
  { key: 'SharedSplitScreen',           label: 'Split Screen (2+)' },
  { key: 'ConsoleGen9Optimized',        label: 'Optimized for X|S' },
  { key: 'Capability4k',                label: '4K Ultra HD' },
  { key: '120fps',                      label: '120 FPS' },
  { key: '60fps',                       label: '60 FPS' },
  { key: 'CapabilityHDR',               label: 'HDR' },
  { key: 'RayTracing',                  label: 'Ray Tracing' },
  { key: 'CapabilityVRR',               label: 'VRR' },
  { key: 'DolbyAtmos',                  label: 'Dolby Atmos' },
  { key: 'SpatialSound',                label: 'Spatial Sound' },
  { key: 'DTSX',                        label: 'DTS:X' },
  { key: 'PcGamePad',                   label: 'PC Gamepad' },
  { key: 'ConsoleKeyboardMouse',        label: 'Keyboard & Mouse' },
  { key: 'XPA',                         label: 'Xbox Play Anywhere' },
  { key: 'XblCrossPlatformMultiPlayer', label: 'Cross-Platform MP' },
  { key: 'ConsoleCrossGen',             label: 'Cross-Gen' },
  { key: 'XblCloudSaves',              label: 'Cloud Saves' },
  { key: 'XblAchievements',            label: 'Achievements' },
];

function CapabilityDropdown({ value, onChange, lang }: { value: string[]; onChange: (v: string[]) => void; lang: string }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!dropRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('scroll', onScroll); };
  }, [open]);

  function updatePos() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left });
    }
  }

  function toggle(key: string) {
    if (value.includes(key)) onChange(value.filter(v => v !== key));
    else onChange([...value, key]);
  }

  const label = value.length === 0
    ? t('filter_features', lang)
    : value.length === 1
      ? capLabel(value[0], lang)
      : `${value.length} ${t('filter_feat_suffix', lang)}`;

  const dropdown = open && dropPos ? (
    <div ref={dropRef} style={{
      position: 'fixed', top: dropPos.top, left: dropPos.left,
      background: 'color-mix(in srgb, var(--v4-bg-elev) 92%, transparent)', backdropFilter: 'blur(22px) saturate(140%)', WebkitBackdropFilter: 'blur(22px) saturate(140%)',
      border: '1px solid var(--v4-border-hover)', borderRadius: 10,
      boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 9999,
      overflow: 'hidden', minWidth: 188,
      maxHeight: 320, overflowY: 'auto',
    }}>
      {CAP_FILTER_OPTIONS.map((opt, idx) => {
        const checked = value.includes(opt.key);
        return (
          <button key={opt.key} onClick={() => toggle(opt.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', padding: '7px 12px',
              background: checked ? 'color-mix(in srgb, var(--gs-green) 12%, transparent)' : 'transparent',
              border: 'none', borderBottom: idx < CAP_FILTER_OPTIONS.length - 1 ? '1px solid var(--v4-border)' : 'none',
              cursor: 'pointer', fontSize: 12,
              fontWeight: checked ? 700 : 400, fontFamily: 'inherit',
              transition: 'background .1s', color: checked ? 'var(--gs-green)' : 'var(--v4-fg-2)',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: 3, flexShrink: 0,
              border: `1.5px solid ${checked ? 'var(--gs-green)' : 'var(--v4-fg-4)'}`,
              background: checked ? 'var(--gs-green)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {checked && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="var(--v4-bg)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </span>
            {capLabel(opt.key, lang)}
          </button>
        );
      })}
      {value.length > 0 && (
        <button onClick={() => { onChange([]); setOpen(false); }}
          style={{
            display: 'block', width: '100%', padding: '7px 12px',
            background: 'rgba(239,68,68,.08)', border: 'none',
            borderTop: '1px solid var(--v4-border)',
            color: '#f87171', fontSize: 11, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {t('filter_features_reset', lang)}
        </button>
      )}
    </div>
  ) : null;

  return (
    <div style={{ flexShrink: 0 }}>
      <button ref={btnRef}
        onClick={() => { if (!open) updatePos(); setOpen(o => !o); }}
        className={`va-chip${value.length > 0 ? ' on' : ''}`}
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', minWidth: 100 }}
      >
        {label}
        <ChevronDown size={10} style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {typeof document !== 'undefined' && dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}

// -- Year Dropdown (multi-select checkboxes) ------------------------------
function YearDropdown({ value, onChange, lang }: { value: number[]; onChange: (v: number[]) => void; lang: string }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010, 2009, 2008, 2007, 2006, 2005];

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!dropRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('scroll', onScroll); };
  }, [open]);

  function updatePos() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left });
    }
  }

  function toggle(y: number) {
    if (value.includes(y)) onChange(value.filter(v => v !== y));
    else onChange([...value, y]);
  }

  const label = value.length === 0 ? t('filter_year', lang) : value.length === 1 ? String(value[0]) : `${value.length} ${t('filter_year_suffix', lang)}`;

  const dropdown = open && dropPos ? (
    <div ref={dropRef} style={{
      position: 'fixed', top: dropPos.top, left: dropPos.left,
      background: 'color-mix(in srgb, var(--v4-bg-elev) 92%, transparent)', backdropFilter: 'blur(22px) saturate(140%)', WebkitBackdropFilter: 'blur(22px) saturate(140%)',
      border: '1px solid var(--v4-border-hover)', borderRadius: 10,
      boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 9999,
      overflow: 'hidden', minWidth: 120,
      maxHeight: 280, overflowY: 'auto',
    }}>
      {YEARS.map((y, idx) => {
        const checked = value.includes(y);
        return (
          <button key={y} onClick={() => toggle(y)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', padding: '7px 12px',
              background: checked ? 'color-mix(in srgb, var(--gs-green) 12%, transparent)' : 'transparent',
              border: 'none', borderBottom: idx < YEARS.length - 1 ? '1px solid var(--v4-border)' : 'none',
              cursor: 'pointer', fontSize: 12,
              fontWeight: checked ? 700 : 400, fontFamily: 'inherit',
              transition: 'background .1s', color: checked ? 'var(--gs-green)' : 'var(--v4-fg-2)',
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: 3, flexShrink: 0,
              border: `1.5px solid ${checked ? 'var(--gs-green)' : 'var(--v4-fg-4)'}`,
              background: checked ? 'var(--gs-green)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {checked && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="var(--v4-bg)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </span>
            {y}
          </button>
        );
      })}
      {value.length > 0 && (
        <button onClick={() => { onChange([]); setOpen(false); }}
          style={{
            display: 'block', width: '100%', padding: '7px 12px',
            background: 'rgba(239,68,68,.08)', border: 'none',
            borderTop: '1px solid var(--v4-border)',
            color: '#f87171', fontSize: 11, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {t('filter_year_reset', lang)}
        </button>
      )}
    </div>
  ) : null;

  return (
    <div style={{ flexShrink: 0 }}>
      <button ref={btnRef}
        onClick={() => { if (!open) updatePos(); setOpen(o => !o); }}
        className={`va-chip${value.length > 0 ? ' on' : ''}`}
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', minWidth: 68 }}
      >
        {label}
        <ChevronDown size={10} style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {typeof document !== 'undefined' && dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}

interface Props { initialItems: CatalogIndexItem[]; totalCount?: number }

export function CatalogShell({ initialItems, totalCount }: Props) {
  const searchParams = useSearchParams();
  const { lang } = usePriceConvert();

  // Start with SSR data immediately — no loading gate. Подальші зміни фільтрів/сторінки
  // йдуть через /api/catalog-page (серверна SQL-пагінація+фільтрація) замість
  // клієнтського MiniSearch по всьому індексу (34к+ товарів, 3.5MB).
  const [items,   setItems]   = useState<CatalogIndexItem[]>(initialItems);
  const [total,   setTotal]   = useState(totalCount ?? initialItems.length);
  const [loading, setLoading] = useState(false);
  const [studios, setStudios] = useState<[string, StudioRole][]>([]);
  const [page,    setPage]    = useState(() => Math.max(1, Number(searchParams.get('page') ?? 1) || 1));

  const [query,       setQuery]       = useState(() => searchParams.get('q') ?? '');
  const [sort,        setSort]        = useState<SortKey>(() => readSort(searchParams));
  const [type,        setType]        = useState<TypeFilter>(() => readType(searchParams));
  const [plat,        setPlat]        = useState<PlatformFilter>(() => readPlat(searchParams));
  const [xsxOnly,       setXsxOnly]       = useState(() => searchParams.get('xsx') === '1');
  const [showXbox360,   setShowXbox360]   = useState(() => searchParams.get('xbox360') === '1');
  const [goldRequired,  setGoldRequired]  = useState(() => searchParams.get('gold') === '1');
  const [genre,       setGenre]       = useState(() => searchParams.get('genre') ?? '');
  const [priceMin,    setPriceMin]    = useState(() => Number(searchParams.get('pmin') ?? 0));
  const [priceMax,    setPriceMax]    = useState(() => { const v = searchParams.get('pmax'); return v ? Number(v) : PRICE_MAX; });
  const [studio,      setStudio]      = useState(() => searchParams.get('studio') ?? '');
  const [offerType, setOfferType] = useState<OfferType>(() => readOfferParams(searchParams).offerType);
  const [region,    setRegion]    = useState<RegionCode>(() => readOfferParams(searchParams).region);
  const [priceMap,  setPriceMap]  = useState<Record<string, number>>({});

  const [genreRowOpen, setGenreRowOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [openSec, setOpenSec] = useState<Set<string>>(() => new Set(['sort', 'type']));
  const toggleSec = (id: string) => setOpenSec(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const Sec = (id: string, label: string, badge: number, content: React.ReactNode) => (
    <div className={`fbar-acc${openSec.has(id) ? ' open' : ''}`}>
      <button className="fbar-acc-head" onClick={() => toggleSec(id)}>
        <span className="fbar-acc-title">{label}{badge > 0 && <span className="fbar-acc-badge">{badge}</span>}</span>
        <ChevronDown size={18} className="fbar-acc-chev" />
      </button>
      <div className="fbar-acc-body"><div className="fbar-acc-inner">{content}</div></div>
    </div>
  );
  const [langFilter,   setLangFilter]   = useState<string[]>([]);
  const [langTypes,    setLangTypes]    = useState<LangType[]>(ALL_LANG_TYPES);
  const [starValue,    setStarValue]    = useState(0);   // 0-5 stars, 0=no filter
  const [selectedYears, setSelectedYears] = useState<number[]>([]); // multi-year filter
  const [selectedCaps,  setSelectedCaps]  = useState<string[]>([]);   // capability filter

  const gridRef = useRef<HTMLDivElement>(null);

  function goToPage(n: number) {
    setPage(n);
    updateUrl({ page: n > 1 ? String(n) : null });
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  useEffect(() => {
    if (region === 'any') { setPriceMap({}); return; }
    setPriceMap({});  // clear stale prices immediately
    fetchPrices(offerType, region).then(setPriceMap);
  }, [offerType, region]);

  // Список студій для автокомпліту — окремий легкий ендпоінт (не весь каталог).
  useEffect(() => { loadStudios().then(setStudios); }, []);

  const updateUrl = useCallback((patches: Record<string, string | null>) => {
    // Use history.replaceState directly — filtering is 100% client-side,
    // no server re-render needed. router.replace() triggers a React transition
    // which can be stale/async in Next.js 16 App Router for shallow param changes.
    const p = new URLSearchParams(window.location.search);
    p.delete('sale');
    for (const [k, v] of Object.entries(patches)) {
      if (v === null || v === '' || v === 'all' || v === 'popular' || v === '0') p.delete(k);
      else p.set(k, v);
    }
    const qs = p.toString();
    window.history.replaceState(null, '', qs ? `/catalog?${qs}` : '/catalog');
  }, []);

  // Головне джерело даних — /api/catalog-page (SQL WHERE+LIMIT/OFFSET на сервері).
  // Дебаунс 250мс — щоб швидкі повторні кліки/введення тексту не стріляли запит на
  // кожну проміжну зміну. Перший рендер зі стандартними фільтрами (без URL-параметрів)
  // пропускає фетч — initialItems/totalCount з SSR вже точна відповідь.
  const isFirstRun = useRef(true);
  const skipFirstFetch = useRef(searchParams.toString() === '').current;
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      if (skipFirstFetch) return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const p = new URLSearchParams();
      if (query.trim().length >= 2) p.set('q', query.trim());
      if (type !== 'all')  p.set('type', type);
      if (plat !== 'all')  p.set('plat', plat);
      if (xsxOnly)          p.set('xsx', '1');
      if (showXbox360)      p.set('xbox360', '1');
      if (goldRequired)     p.set('gold', '1');
      if (genre)            p.set('genre', genre);
      if (priceMin > 0)          p.set('pmin', String(priceMin));
      if (priceMax < PRICE_MAX)  p.set('pmax', String(priceMax));
      if (studio.trim())    p.set('studio', studio.trim());
      if (starValue > 0)    p.set('stars', String(starValue));
      if (selectedYears.length) p.set('years', selectedYears.join(','));
      if (selectedCaps.length)  p.set('caps', selectedCaps.join(','));
      if (langFilter.length && langTypes.length) {
        p.set('langs', langFilter.join(','));
        p.set('langTypes', langTypes.join(','));
      }
      if (sort !== 'popular') p.set('sort', sort);
      if (page > 1) p.set('page', String(page));

      setLoading(true);
      fetch(`/api/catalog-page?${p.toString()}`, { signal: controller.signal, credentials: 'same-origin' })
        .then(r => r.json())
        .then(data => { setItems(data.items ?? []); setTotal(data.total ?? 0); })
        .catch(e => { if ((e as Error).name !== 'AbortError') console.error('[catalog] fetch failed:', e); })
        .finally(() => setLoading(false));
    }, 250);
    return () => { clearTimeout(timer); controller.abort('filters changed'); };
  }, [query, type, plat, xsxOnly, showXbox360, goldRequired, genre, priceMin, priceMax,
      studio, starValue, selectedYears, selectedCaps, langFilter, langTypes, sort, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e: Event) => {
      const q = (e as CustomEvent<string>).detail;
      setQuery(q); resetVisible(); updateUrl({ q: q || null });
    };
    window.addEventListener('catalog:search', handler);
    return () => window.removeEventListener('catalog:search', handler);
  }, [updateUrl]);

  function resetVisible() { setPage(1); updateUrl({ page: null }); }

  function handleSort(s: SortKey)             { setSort(s);   resetVisible(); updateUrl({ sort: s === 'popular' ? null : s }); }
  function toggleSort(a: SortKey, b: SortKey) {
    const next: SortKey = sort === a ? b : sort === b ? 'popular' : a;
    handleSort(next);
  }
  function handleType(t: TypeFilter)          { setType(t);   resetVisible(); updateUrl({ type: t }); }
  function handlePlat(p: PlatformFilter)      { setPlat(p);   resetVisible(); updateUrl({ plat: p }); }
  function handleXsx()     { const n = !xsxOnly;     setXsxOnly(n);     resetVisible(); updateUrl({ xsx: n ? '1' : null }); }
  function handleXbox360() { const n = !showXbox360; setShowXbox360(n); resetVisible(); updateUrl({ xbox360: n ? '1' : null }); }
  function handleGold()    { const n = !goldRequired; setGoldRequired(n); resetVisible(); updateUrl({ gold: n ? '1' : null }); }
  function handleGenre(g: string)             { setGenre(g);  resetVisible(); updateUrl({ genre: g || null }); }
  function handlePriceMin(v: number)          { setPriceMin(v); resetVisible(); updateUrl({ pmin: v > 0 ? String(v) : null }); }
  function handlePriceMax(v: number)          { setPriceMax(v); resetVisible(); updateUrl({ pmax: v < PRICE_MAX ? String(v) : null }); }
  function handleStudio(s: string)            { setStudio(s); resetVisible(); updateUrl({ studio: s.trim() || null }); }

  function resetAll() {
    setSort('popular'); setType('all'); setPlat('all');
    setXsxOnly(false); setShowXbox360(false); setGoldRequired(false);
    setGenre(''); setPriceMin(0); setPriceMax(PRICE_MAX); setStudio('');
    setLangFilter([]); setLangTypes(ALL_LANG_TYPES);
    setStarValue(0); setSelectedYears([]); setSelectedCaps([]);
    resetVisible();
    updateUrl({ sort:null, type:null, plat:null, xsx:null, xbox360:null, gold:null,
                genre:null, pmin:null, pmax:null, studio:null });
  }

  const activeCount = [
    type !== 'all', plat !== 'all', xsxOnly, showXbox360, goldRequired,
    genre !== '', priceMin > 0, priceMax < PRICE_MAX, studio.trim() !== '',
    sort !== 'popular', langFilter.length > 0, starValue > 0, selectedYears.length > 0, selectedCaps.length > 0,
  ].filter(Boolean).length;

  const chip     = useCallback((active: boolean) => `va-chip${active ? ' on' : ''}`, []);
  const platChip = useCallback((p: PlatformFilter, active: boolean) =>
    active ? `va-chip ${PLAT_CHIP[p]}` : 'va-chip', []);

  // Фільтрація+пагінація тепер повністю на сервері (/api/catalog-page) — items/total
  // вже точна відповідь під поточну сторінку+фільтри, без клієнтського slice/filter.
  const filtered   = items;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage   = page;
  const visible    = items;

  return (
    <div>
      {/* -- Mobile filter trigger (bottom-sheet) -- */}
      <div className="fbar-mobile">
        <button className={`fbar-m-btn${activeCount > 0 ? ' on' : ''}`} onClick={() => setSheetOpen(true)}>
          <SlidersHorizontal size={15} /> {t('m_filters', lang)}
          {activeCount > 0 && <span className="fbar-m-badge">{activeCount}</span>}
        </button>
        <span className="fbar-m-count">{fmtCount(total)}</span>
      </div>

      {/* -- Filter bar -- */}
      <div className="filter-bar">
        <div className="filter-bar-inner">

          {/* Row 1: Sort + Price slider */}
          <div className="fbar-row-wrap">
          <div className="fbar-row">
            <span className="fbar-label">{t('filter_sort', lang)}</span>
            <button className={chip(sort === 'popular')} onClick={() => handleSort('popular')}>{t('sort_popular', lang)}</button>
            <button className={chip(sort === 'new')} onClick={() => handleSort('new')}>{t('sort_new', lang)}</button>
            <button className={chip(sort === 'rating_desc' || sort === 'rating_asc')}
              onClick={() => toggleSort('rating_desc', 'rating_asc')}>
              {sort === 'rating_desc' ? t('sort_rating_desc', lang) : sort === 'rating_asc' ? t('sort_rating_asc', lang) : t('filter_rating', lang)}
            </button>
            <button className={chip(sort === 'price_asc' || sort === 'price_desc')}
              onClick={() => toggleSort('price_asc', 'price_desc')}>
              {sort === 'price_asc' ? t('sort_price_asc', lang) : sort === 'price_desc' ? t('sort_price_desc', lang) : t('filter_price', lang)}
            </button>
            <button className={chip(sort === 'discount_desc' || sort === 'discount_asc')}
              onClick={() => toggleSort('discount_desc', 'discount_asc')}>
              {sort === 'discount_desc' ? t('sort_discount_desc', lang) : sort === 'discount_asc' ? t('sort_discount_asc', lang) : t('filter_discount', lang)}
            </button>
            <div className="fbar-sep" />
            <LanguageFilter
              langs={langFilter}
              types={langTypes}
              onChange={(l, lt) => { setLangFilter(l); setLangTypes(lt); resetVisible(); }}
            />
            <div className="fbar-sep" />
            <span className="fbar-label">{t('filter_rating', lang)}</span>
            <StarRating value={starValue} onChange={v => { setStarValue(v); if (v > 0 && sort === "popular") setSort("rating_asc"); resetVisible(); }} lang={lang} />
            <div className="fbar-sep" />
            <YearDropdown value={selectedYears} onChange={v => { setSelectedYears(v); resetVisible(); }} lang={lang} />
            <div className="fbar-sep" />
            <CapabilityDropdown value={selectedCaps} onChange={v => { setSelectedCaps(v); resetVisible(); }} lang={lang} />
            <div className="fbar-sep" />
            <OfferFilter basePath="/catalog" offerType={offerType} region={region} onOfferChange={(o, r) => { setOfferType(o); setRegion(r); setPage(1); }} />
          </div>
            {/* Слайдер + лічильник поза скрол-рядом — завжди повністю видимі */}
            <PriceRangeSlider priceMin={priceMin} priceMax={priceMax}
              onMinChange={handlePriceMin} onMaxChange={handlePriceMax} />
            <span className="fbar-label fbar-count">
              {fmtCount(total)}
            </span>
          </div>

          {/* Row 2: Type + Platform + Specials + Studio + Reset + Genres toggle */}
          <div className="fbar-row fbar-row-2">
            <span className="fbar-label">{t('filter_type', lang)}</span>
            {TYPE_KEYS.map(tk => (
              <button key={tk} className={chip(type === tk)} onClick={() => handleType(tk)}>
                {tk === 'all' ? t('catalog_type_all', lang) : tk === 'game' ? t('catalog_type_game', lang) : t('catalog_type_dlc', lang)}
              </button>
            ))}
            <div className="fbar-sep" />
            <span className="fbar-label">{t('filter_platform', lang)}</span>
            {PLAT_KEYS.map(p => (
              <button key={p} className={platChip(p, plat === p)} onClick={() => handlePlat(p)}>
                {p === 'all' ? t('platform_all', lang) : PLAT_LABEL[p]}
              </button>
            ))}
            <div className="fbar-sep" />
            <button className={chip(xsxOnly)} onClick={handleXsx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Zap size={11} />{t('catalog_optimized', lang)}
            </button>
            <button className={`va-chip${goldRequired ? ' on-gold' : ''}`} onClick={handleGold} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Shield size={11} />{t('filter_gold', lang)}
            </button>
            <button className={chip(showXbox360)} onClick={handleXbox360}>Xbox 360</button>
            <div className="fbar-sep" />
            <StudioAutocomplete value={studio} onChange={handleStudio} studios={studios} lang={lang} />
            <div style={{ flex: 1, minWidth: 8 }} />
            {activeCount > 0 && (
              <button className="fbar-reset-btn" onClick={resetAll}>{t('catalog_reset_active', lang)} ({activeCount})</button>
            )}
            <button
              className={`va-chip${genreRowOpen || genre !== '' ? ' on' : ''}`}
              onClick={() => setGenreRowOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
            >
              {t('filter_genres', lang)}
              <ChevronDown size={10} style={{
                transition: 'transform .2s ease',
                transform: genreRowOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }} />
            </button>
          </div>
          {/* Expandable genre row */}
          <div className={`fbar-genre-expand${genreRowOpen ? " open" : ""}`}>
            <div className="fbar-row fbar-genre-row-inner">
              <button className={chip(genre === '')} onClick={() => setGenre('')} style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                <LayoutGrid size={11} strokeWidth={2} />
                {t('filter_all_genres', lang)}
              </button>
              {GENRES.map(({ key, i18nKey, Icon }) => (
                <button key={key} className={chip(genre === key)}
                  onClick={() => setGenre(genre === key ? '' : key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                  <Icon size={11} strokeWidth={2} />
                  {t(i18nKey, lang)}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* -- Mobile filter bottom-sheet -- */}
      <div className={`fbar-scrim${sheetOpen ? ' open' : ''}`} onClick={() => setSheetOpen(false)} />
      <div className={`fbar-sheet${sheetOpen ? ' open' : ''}`} role="dialog" aria-modal="true" aria-label={t('m_filters', lang)}>
        <div className="fbar-sheet-grip" />
        <div className="fbar-sheet-head">
          <span className="fbar-sheet-title">{t('m_filters', lang)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {activeCount > 0 && <button className="fbar-sheet-reset" onClick={resetAll}>{t('m_reset', lang)} ({activeCount})</button>}
            <button className="fbar-sheet-x" aria-label="✕" onClick={() => setSheetOpen(false)}><X size={16} /></button>
          </div>
        </div>
        <div className="fbar-sheet-body">
          {/* Quick on/off filters (booleans) — always-visible chips (tap on/off) */}
          <div className="fbar-quick fbar-group-chips">
            <button className={chip(xsxOnly)} onClick={handleXsx} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Zap size={13} />{t('catalog_optimized', lang)}</button>
            <button className={`va-chip${goldRequired ? ' on-gold' : ''}`} onClick={handleGold} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Shield size={13} />{t('filter_gold', lang)}</button>
            <button className={chip(showXbox360)} onClick={handleXbox360}>Xbox 360</button>
          </div>
          {Sec('sort', t('m_sort', lang), 0, (
            <div className="fbar-group-chips">
              <button className={chip(sort === 'popular')} onClick={() => handleSort('popular')}>{t('sort_popular', lang)}</button>
              <button className={chip(sort === 'new')} onClick={() => handleSort('new')}>{t('sort_new', lang)}</button>
              <button className={chip(sort === 'rating_desc' || sort === 'rating_asc')} onClick={() => toggleSort('rating_desc', 'rating_asc')}>{t('filter_rating', lang)}</button>
              <button className={chip(sort === 'price_asc' || sort === 'price_desc')} onClick={() => toggleSort('price_asc', 'price_desc')}>{t('filter_price', lang)}</button>
              <button className={chip(sort === 'discount_desc' || sort === 'discount_asc')} onClick={() => toggleSort('discount_desc', 'discount_asc')}>{t('filter_discount', lang)}</button>
            </div>
          ))}
          {Sec('type', t('filter_type', lang), type !== 'all' ? 1 : 0, (
            <div className="fbar-group-chips">
              {TYPE_KEYS.map(tk => (
                <button key={tk} className={chip(type === tk)} onClick={() => handleType(tk)}>
                  {tk === 'all' ? t('catalog_type_all', lang) : tk === 'game' ? t('catalog_type_game', lang) : t('catalog_type_dlc', lang)}
                </button>
              ))}
            </div>
          ))}
          {Sec('platform', t('filter_platform', lang), plat !== 'all' ? 1 : 0, (
            <div className="fbar-group-chips">
              {PLAT_KEYS.map(p => (
                <button key={p} className={platChip(p, plat === p)} onClick={() => handlePlat(p)}>
                  {p === 'all' ? t('platform_all', lang) : PLAT_LABEL[p]}
                </button>
              ))}
            </div>
          ))}
          {Sec('genre', t('filter_genres', lang), genre !== '' ? 1 : 0, (
            <div className="fbar-group-chips">
              <button className={chip(genre === '')} onClick={() => setGenre('')} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <LayoutGrid size={12} /> {t('filter_all_genres', lang)}
              </button>
              {GENRES.map(({ key, i18nKey, Icon }) => (
                <button key={key} className={chip(genre === key)} onClick={() => setGenre(genre === key ? '' : key)} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon size={12} /> {t(i18nKey as never, lang)}
                </button>
              ))}
            </div>
          ))}
          {Sec('price', t('filter_price', lang), (priceMin > 0 || priceMax < PRICE_MAX) ? 1 : 0, (
            <div className="fbar-price">
              <div className="fbar-price-vals">
                <span>${priceMin}</span>
                <span>{priceMax >= PRICE_MAX ? '∞' : `$${priceMax}`}</span>
              </div>
              <div className="price-range-wrap">
                <div className="price-range-track"><div className="price-range-fill" style={{ left: `${(priceMin / PRICE_MAX) * 100}%`, right: `${100 - (priceMax / PRICE_MAX) * 100}%` }} /></div>
                <input type="range" min={0} max={PRICE_MAX} step={1} value={priceMin} className="price-range-input" style={{ zIndex: 3 }} onChange={e => handlePriceMin(Math.min(+e.target.value, priceMax - 1))} />
                <input type="range" min={0} max={PRICE_MAX} step={1} value={priceMax} className="price-range-input" style={{ zIndex: 3 }} onChange={e => handlePriceMax(Math.max(+e.target.value, priceMin + 1))} />
              </div>
            </div>
          ))}
          {Sec('rating', t('filter_rating', lang), starValue > 0 ? 1 : 0, (
            <div className="fbar-rating">
              {[1, 2, 3, 4, 5].map(sv => (
                <button key={sv} className={`va-star${sv <= starValue ? ' on' : ''}`} aria-label={`★${sv}`}
                  onClick={() => { const v = sv === starValue ? 0 : sv; setStarValue(v); if (v > 0 && sort === 'popular') setSort('rating_asc'); resetVisible(); }}>★</button>
              ))}
            </div>
          ))}
          {Sec('offer', t('m_offer', lang), offerType !== 'key' ? 1 : 0, (
            <div className="fbar-group-chips">
              {(['key', 'gift', 'account', 'rental'] as OfferType[]).map(o => (
                <button key={o} className={chip(offerType === o)} onClick={() => { setOfferType(o); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {OFFER_ICONS[o]} {t(`offer_${o}` as never, lang)}
                </button>
              ))}
            </div>
          ))}
          {Sec('region', t('m_region', lang), region !== 'any' ? 1 : 0, (
            <div className="fbar-group-chips">
              {ALL_REGIONS.map(r => (
                <button key={r.code} className={chip(region === r.code)} onClick={() => { setRegion(r.code); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {r.code === 'any' ? t('platform_all', lang) : <>{r.flag} <span suppressHydrationWarning>{regionName(r.code, lang)}</span></>}
                </button>
              ))}
            </div>
          ))}
          {Sec('year', t('filter_year', lang), selectedYears.length > 0 ? 1 : 0, (
            <div className="fbar-group-chips">
              {[2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010, 2009, 2008, 2007, 2006, 2005].map(y => (
                <button key={y} className={chip(selectedYears.includes(y))} onClick={() => { setSelectedYears(selectedYears.includes(y) ? selectedYears.filter(x => x !== y) : [...selectedYears, y]); resetVisible(); }}>{y}</button>
              ))}
            </div>
          ))}
          {Sec('caps', t('m_caps', lang), selectedCaps.length > 0 ? 1 : 0, (
            <div className="fbar-group-chips">
              {Object.values(CAP_MAP).sort((a, b) => a.priority - b.priority).map(c => (
                <button key={c.key} className={chip(selectedCaps.includes(c.key))} onClick={() => { setSelectedCaps(selectedCaps.includes(c.key) ? selectedCaps.filter(x => x !== c.key) : [...selectedCaps, c.key]); resetVisible(); }}>
                  {lang === 'ru' ? c.ru : lang === 'uk' ? c.uk : c.en}
                </button>
              ))}
            </div>
          ))}
          {Sec('lang', t('m_lang', lang), langFilter.length > 0 ? 1 : 0, (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Тип відповідності: аудіо / субтитри / інтерфейс (як на десктопі) */}
              <div className="fbar-group-chips">
                {(['a', 's', 'i'] as LangType[]).map(tk => (
                  <button key={tk} className={chip(langTypes.includes(tk))}
                    onClick={() => { const nt = langTypes.includes(tk) ? langTypes.filter(x => x !== tk) : [...langTypes, tk]; setLangTypes(nt.length ? nt : ALL_LANG_TYPES); resetVisible(); }}>
                    {tk === 'a' ? t('filter_lang_audio', lang) : tk === 's' ? t('filter_lang_subs', lang) : t('filter_lang_interface', lang)}
                  </button>
                ))}
              </div>
              <div className="fbar-group-chips">
                {LANG_LIST.map(l => (
                  <button key={l.code} className={chip(langFilter.includes(l.code))} onClick={() => { const nf = langFilter.includes(l.code) ? langFilter.filter(x => x !== l.code) : [...langFilter, l.code]; setLangFilter(nf); if (langTypes.length === 0) setLangTypes(ALL_LANG_TYPES); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {l.flag} <span suppressHydrationWarning>{langName(l.code, lang, l.label)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {Sec('dev', t('pdp_tech_developer', lang), studio ? 1 : 0, (
            <div className="fbar-studio-mobile">
              <Search size={15} />
              <input placeholder={`${t('pdp_tech_developer', lang)}...`} value={studio} onChange={e => handleStudio(e.target.value)} autoComplete="off" />
            </div>
          ))}
        </div>
        <div className="fbar-sheet-foot">
          <button className="fbar-sheet-apply" onClick={() => setSheetOpen(false)}>
            {t('m_show', lang)} {fmtCount(total)}
          </button>
        </div>
      </div>

      {/* -- Grid -- */}
      <main style={{ maxWidth: 1560, margin: '0 auto', padding: '20px 24px 80px' }}>
        {filtered.length === 0 && !loading ? (
          <div className="empty-state">{t('catalog_no_results', lang)}</div>
        ) : filtered.length === 0 ? null : (
          <>
            <div ref={gridRef} className="catalog-grid">
              {visible.map(item => (
                <CatalogCard key={item.product_id} item={item} lang={lang} offerType={offerType} region={region} priceUsdOverride={region !== 'any' ? priceMap[item.product_id] : undefined} />
              ))}
            </div>

            <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
          </>
        )}
      </main>
    </div>
  );
}
